import { DashboardCard } from "./dashboard-card";
import { useAccountContext } from "../account-context";
import { usePnlHistory } from "../hooks/use-pnl-history";
import { formatCurrency, cn, isValidSolanaAddress } from "@/lib/utils";
import { useThrottledUnrealizedPnL } from "../hooks/use-throttled-unrealized-pnl";
import {
  CopyIcon,
  CheckIcon,
  ArrowRightIcon,
  PlusCircleIcon,
  ArrowDownCircleIcon,
  Loader2Icon,
  StarIcon,
  TrashIcon,
  CheckCircle2Icon,
  ExternalLink,
} from "lucide-react";
import { Spinner } from "@/components/spinner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DepositButton, type DepositButtonHandle } from "@/header/deposit-button";
import { WithdrawalsApi, WithdrawalAddressesApi } from "@/backend/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { useWallet } from "@solana/wallet-adapter-react";
import type { SavedWithdrawalAddress } from "@/backend/types";

// Site's green color - brand color for positive actions
const SITE_GREEN = "#2ebd85";

export const PortfolioValueCard = () => {
  const { userId, accountSummary, fills, fundingPayments, isLoading } = useAccountContext();
  const { summary: pnlSummary } = usePnlHistory({ fills, fundingPayments });
  const { authMethod } = useUnifiedAuth();
  const { publicKey } = useWallet();
  const [copied, setCopied] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const depositButtonRef = useRef<DepositButtonHandle>(null);
  const queryClient = useQueryClient();
  
  // Use throttled unrealized P&L for portfolio page (updates every 10 seconds)
  // This reduces visual noise while still keeping values reasonably current
  const realtimeUnrealizedPnl = useThrottledUnrealizedPnL(10000);

  // Email user withdrawal address state
  const [destinationAddressInput, setDestinationAddressInput] = useState("");
  const [savedAddresses, setSavedAddresses] = useState<SavedWithdrawalAddress[]>([]);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState<string>("");
  const [saveThisAddress, setSaveThisAddress] = useState(false);
  const [addressLabel, setAddressLabel] = useState("");
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [isDeletingAddress, setIsDeletingAddress] = useState<string | null>(null);

  // Token selection state
  const [selectedToken, setSelectedToken] = useState<"USDC" | "USDT">("USDC");

  // Pending withdrawal state
  const [pendingWithdrawal, setPendingWithdrawal] = useState<any | null>(null);

  // Get destination address based on auth method
  const getDestinationAddress = () => {
    if (authMethod === "wallet") {
      return publicKey?.toBase58() || null;
    }
    // For email users, use selected saved address or manual input
    if (selectedSavedAddressId && selectedSavedAddressId !== "new") {
      const saved = savedAddresses.find((a) => a.id === selectedSavedAddressId);
      return saved?.address || null;
    }
    return destinationAddressInput || null;
  };

  // Load saved addresses when dialog opens (for email users)
  useEffect(() => {
    if (withdrawOpen && authMethod === "privy" && userId) {
      setIsLoadingAddresses(true);
      WithdrawalAddressesApi.listAddresses()
        .then((addresses) => {
          setSavedAddresses(addresses);
          if (addresses.length > 0) {
            setSelectedSavedAddressId(addresses[0].id);
          } else {
            setSelectedSavedAddressId("new");
          }
        })
        .catch((error) => {
          console.error("Failed to load saved addresses:", error);
        })
        .finally(() => {
          setIsLoadingAddresses(false);
        });
    }
  }, [withdrawOpen, authMethod, userId]);

  // Fetch pending withdrawals when dialog opens
  useEffect(() => {
    if (withdrawOpen && userId) {
      WithdrawalsApi.listWithdrawals()
        .then((withdrawals) => {
          // Find the most recent pending/processing withdrawal
          const pending = withdrawals.find(
            (w) => w.status === "pending" || w.status === "processing" || w.status === "approved"
          );
          setPendingWithdrawal(pending || null);
        })
        .catch((error) => {
          console.error("Failed to load withdrawals:", error);
        });
    }
  }, [withdrawOpen, userId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!withdrawOpen) {
      setDestinationAddressInput("");
      setSelectedSavedAddressId("");
      setSaveThisAddress(false);
      setAddressLabel("");
      setWithdrawAmount("");
      setSelectedToken("USDC");
    }
  }, [withdrawOpen]);

  const handleCopyUid = async () => {
    if (!userId) return;
    try {
      await navigator.clipboard.writeText(userId);
      setCopied(true);
      toast.success("User ID copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleWithdraw = async () => {
    const destAddress = getDestinationAddress();
    if (!userId || !destAddress || !withdrawAmount) return;

    // Validate address for email users
    if (authMethod === "privy" && !isValidSolanaAddress(destAddress)) {
      toast.error("Please enter a valid Solana wallet address");
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setIsWithdrawing(true);
    try {
      // Save address if checkbox is checked (for email users with new address)
      if (
        authMethod === "privy" &&
        saveThisAddress &&
        (selectedSavedAddressId === "new" || !selectedSavedAddressId) &&
        destinationAddressInput
      ) {
        try {
          const savedAddress = await WithdrawalAddressesApi.saveAddress(
            destinationAddressInput,
            addressLabel || "Saved Address"
          );
          setSavedAddresses((prev) => [savedAddress, ...prev]);
          toast.success("Address saved for future withdrawals");
        } catch (error) {
          console.error("Failed to save address:", error);
        }
      }

      await WithdrawalsApi.createWithdrawal(destAddress, amount, undefined, selectedToken);
      toast.success(
        <span>
          Withdrawal of{" "}
          <span className="text-emerald-400 font-semibold">
            ${amount.toLocaleString()} {selectedToken}
          </span>{" "}
          is being processed.
        </span>
      );
      queryClient.invalidateQueries({ queryKey: ["account-summary"] });
      queryClient.invalidateQueries({ queryKey: ["withdrawals"] });
      setWithdrawAmount("");
      setWithdrawOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Withdrawal failed"
      );
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleDeleteSavedAddress = async (addressId: string) => {
    setIsDeletingAddress(addressId);
    try {
      await WithdrawalAddressesApi.deleteAddress(addressId);
      setSavedAddresses((prev) => prev.filter((a) => a.id !== addressId));
      if (selectedSavedAddressId === addressId) {
        setSelectedSavedAddressId(savedAddresses.length > 1 ? savedAddresses[0].id : "new");
      }
      toast.success("Address removed");
    } catch (_error) {
      toast.error("Failed to remove address");
    } finally {
      setIsDeletingAddress(null);
    }
  };

  if (isLoading) {
    return (
      <DashboardCard title="Account Value" subtitle="Total account equity">
        <div className="flex items-center justify-center h-32">
          <Spinner />
        </div>
      </DashboardCard>
    );
  }

  // Use real-time unrealized P&L for accurate portfolio value
  // This is calculated from positions in the Zustand store + current market prices
  const unrealizedPnl = realtimeUnrealizedPnl;
  const realizedPnl = accountSummary?.realizedPnl ?? 0;
  const equity = accountSummary ? accountSummary.collateral + unrealizedPnl : 0;
  const totalPnl = unrealizedPnl + realizedPnl;
  const pnlPercent = accountSummary?.collateral
    ? (totalPnl / accountSummary.collateral) * 100
    : 0;

  const isPositive = totalPnl >= 0;

  // Check if the address is valid for withdrawal
  const isAddressValid = authMethod === "wallet" 
    ? !!publicKey 
    : (selectedSavedAddressId && selectedSavedAddressId !== "new") 
      ? true 
      : isValidSolanaAddress(destinationAddressInput);

  return (
    <>
      <DashboardCard
        title="Account Value"
        subtitle="Total account equity and performance"
        className="bg-gradient-to-br from-card via-card to-primary/5 h-full"
      >
        <div className="flex flex-col h-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 flex-1">
            {/* Main Value */}
            <div>
              <div className="text-muted-foreground text-sm mb-1">
                Total Equity
              </div>
              <div className="text-3xl sm:text-4xl font-bold tracking-tight">
                ${formatCurrency(equity, 2)}
              </div>
              <div
                className={cn(
                  "flex items-center gap-1 mt-2 text-sm font-medium",
                  isPositive ? "text-bid-foreground" : "text-ask-foreground"
                )}
              >
                <span>
                  {isPositive ? "+" : ""}
                  {formatCurrency(totalPnl, 2)} ({pnlPercent.toFixed(2)}%)
                </span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                  Unrealized P&L
                </div>
                <div
                  className={cn(
                    "text-lg font-semibold",
                    unrealizedPnl >= 0 ? "text-bid-foreground" : "text-ask-foreground"
                  )}
                >
                  {unrealizedPnl >= 0 ? "+" : ""}${formatCurrency(Math.abs(unrealizedPnl), 2)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                  Realized P&L
                </div>
                <div
                  className={cn(
                    "text-lg font-semibold",
                    realizedPnl >= 0 ? "text-bid-foreground" : "text-ask-foreground"
                  )}
                >
                  {realizedPnl >= 0 ? "+" : ""}${formatCurrency(Math.abs(realizedPnl), 2)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                  Collateral
                </div>
                <div className="text-lg font-semibold">
                  ${formatCurrency(accountSummary?.collateral ?? 0, 2)}
                </div>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">
                  24h Change
                </div>
                <div
                  className={cn(
                    "text-lg font-semibold",
                    pnlSummary.change24h >= 0 ? "text-bid-foreground" : "text-ask-foreground"
                  )}
                >
                  {pnlSummary.change24h >= 0 ? "+" : ""}
                  ${formatCurrency(Math.abs(pnlSummary.change24h), 2)}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section: User ID + Quick Actions */}
          <div className="mt-6 pt-4 border-t border-border/50 space-y-3">
            {/* Row 1: UID + Trade (always on same row) */}
            <div className="flex items-center justify-between gap-4">
              {/* User ID */}
              {userId && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">UID:</span>
                  <button
                    onClick={handleCopyUid}
                    className="flex items-center gap-1.5 text-sm bg-muted/50 hover:bg-muted px-2 py-1 rounded transition-colors"
                  >
                    <span>{userId.slice(0, 6)}...{userId.slice(-4)}</span>
                    {copied ? (
                      <CheckIcon className="size-3 text-bid-foreground" />
                    ) : (
                      <CopyIcon className="size-3 text-muted-foreground" />
                    )}
                  </button>
                </div>
              )}

              {/* Trade Button */}
              <Link to="/">
                <Button variant="outline" size="sm" className="gap-1.5">
                  <ArrowRightIcon className="size-3.5" />
                  Trade
                </Button>
              </Link>
            </div>

            {/* Row 2: Deposit + Withdraw */}
            <div className="flex items-center gap-2">
              <DepositButton
                ref={depositButtonRef}
                trigger={
                  <Button variant="outline" size="sm" className="flex-1 gap-1.5 text-bid-foreground border-bid-foreground/30 hover:bg-bid/10">
                    <PlusCircleIcon className="size-3.5" />
                    Deposit
                  </Button>
                }
              />
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-1.5"
                onClick={() => setWithdrawOpen(true)}
              >
                <ArrowDownCircleIcon className="size-3.5" />
                Withdraw
              </Button>
            </div>
          </div>
        </div>
      </DashboardCard>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Withdraw Funds</DialogTitle>
            <DialogDescription>
              Withdraw USDC or USDT from your trading account to {authMethod === "wallet" ? "your connected wallet" : "any Solana wallet"}. You can only withdraw tokens you have deposited.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Pending Withdrawal Status */}
            {pendingWithdrawal && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <Loader2Icon className="size-5 text-amber-500 animate-spin" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                      Withdrawal In Progress
                    </p>
                    <p className="text-xs text-muted-foreground">
                      ${parseFloat(pendingWithdrawal.amount).toFixed(2)} USDC to{" "}
                      {pendingWithdrawal.destination_address.slice(0, 4)}...
                      {pendingWithdrawal.destination_address.slice(-4)}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Status: {pendingWithdrawal.status}
                    </p>
                    {pendingWithdrawal.tx_signature && (
                      <a
                        href={`https://solscan.io/tx/${pendingWithdrawal.tx_signature}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 flex items-center gap-1 mt-1"
                      >
                        View on Solscan <ExternalLink className="size-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Available Balance Card */}
            <div 
              className="rounded-lg p-4 border"
              style={{ 
                backgroundColor: `${SITE_GREEN}12`,
                borderColor: `${SITE_GREEN}30`
              }}
            >
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
                Available to Withdraw (USD)
              </p>
              <p className="text-xl font-semibold">
                ${formatCurrency(accountSummary?.availableBalance ?? 0, 2)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Funds locked in positions: ${formatCurrency((accountSummary?.collateral ?? 0) - (accountSummary?.availableBalance ?? 0), 2)}
              </p>
            </div>

            {/* Destination Address Section */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Destination Wallet
              </label>
              
              {authMethod === "wallet" ? (
                // Wallet users: show connected wallet address
                <>
                  <div className="bg-muted/50 border rounded-lg p-3 text-xs break-all text-muted-foreground">
                    {publicKey?.toBase58() || "Connect wallet to withdraw"}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Funds will be sent to your connected wallet address.
                  </p>
                </>
              ) : (
                // Email users: show saved addresses dropdown + manual input
                <>
                  {isLoadingAddresses ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2Icon className="size-4 animate-spin" />
                      Loading saved addresses...
                    </div>
                  ) : (
                    <>
                      {/* Saved Addresses Dropdown */}
                      <Select
                        value={selectedSavedAddressId}
                        onValueChange={setSelectedSavedAddressId}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select or enter a new address" />
                        </SelectTrigger>
                        <SelectContent>
                          {savedAddresses.map((addr) => (
                            <SelectItem key={addr.id} value={addr.id}>
                              <div className="flex items-center gap-2">
                                <StarIcon className="size-3 text-amber-500" />
                                <span className="font-medium">{addr.label}</span>
                                <span className="text-muted-foreground text-xs">
                                  ({addr.address.slice(0, 4)}...{addr.address.slice(-4)})
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                          <SelectItem value="new">
                            Enter a new address
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      {/* Show selected saved address details or new address input */}
                      {selectedSavedAddressId && selectedSavedAddressId !== "new" ? (
                        <div className="space-y-2">
                          <div className="bg-muted/50 border rounded-lg p-3 text-xs break-all">
                            {savedAddresses.find((a) => a.id === selectedSavedAddressId)?.address}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteSavedAddress(selectedSavedAddressId)}
                            disabled={isDeletingAddress === selectedSavedAddressId}
                            className="flex items-center gap-1 text-xs text-rose-500 hover:text-rose-600"
                          >
                            {isDeletingAddress === selectedSavedAddressId ? (
                              <Loader2Icon className="size-3 animate-spin" />
                            ) : (
                              <TrashIcon className="size-3" />
                            )}
                            Remove saved address
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Input
                            placeholder="Enter Solana wallet address"
                            value={destinationAddressInput}
                            onChange={(e) => setDestinationAddressInput(e.target.value)}
                            className="text-xs"
                          />
                          {destinationAddressInput && !isValidSolanaAddress(destinationAddressInput) && (
                            <p className="text-xs text-rose-500">
                              Please enter a valid Solana wallet address
                            </p>
                          )}
                          
                          {/* Save address checkbox */}
                          {destinationAddressInput && isValidSolanaAddress(destinationAddressInput) && (
                            <div className="space-y-2 p-3 bg-muted/30 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="save-address-portfolio"
                                  checked={saveThisAddress}
                                  onCheckedChange={(checked: boolean | "indeterminate") => setSaveThisAddress(checked === true)}
                                />
                                <label htmlFor="save-address-portfolio" className="text-xs cursor-pointer">
                                  Save this address for future withdrawals
                                </label>
                              </div>
                              {saveThisAddress && (
                                <Input
                                  placeholder="Address label (e.g., My Phantom Wallet)"
                                  value={addressLabel}
                                  onChange={(e) => setAddressLabel(e.target.value)}
                                  className="text-xs"
                                  maxLength={50}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Token Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Token</label>
              <Select
                value={selectedToken}
                onValueChange={(value: "USDC" | "USDT") => setSelectedToken(value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select token" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USDC">
                    <div className="flex items-center gap-2">
                      <img src="/icons/tokens/usdc.svg" alt="USDC" className="size-5 rounded-full" />
                      <span>USDC</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="USDT">
                    <div className="flex items-center gap-2">
                      <img src="/icons/tokens/usdt.svg" alt="USDT" className="size-5 rounded-full" />
                      <span>USDT</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select the token you deposited to withdraw it in the same token.
              </p>
            </div>

            {/* Amount Input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Amount ({selectedToken})</label>
                <button 
                  type="button"
                  className="text-xs font-medium hover:opacity-80 cursor-pointer transition-opacity"
                  style={{ color: SITE_GREEN }}
                  onClick={() => setWithdrawAmount((accountSummary?.availableBalance ?? 0).toString())}
                  disabled={!accountSummary?.availableBalance || accountSummary.availableBalance <= 0}
                >
                  Max
                </button>
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={withdrawAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Allow empty or valid numbers with max 2 decimals
                    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                      setWithdrawAmount(value);
                    }
                  }}
                  className="pl-7 text-lg"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div 
              className="rounded-lg p-3 border"
              style={{ 
                backgroundColor: `${SITE_GREEN}12`,
                borderColor: `${SITE_GREEN}30`
              }}
            >
              <p 
                className="text-xs flex items-center gap-1"
                style={{ color: SITE_GREEN }}
              >
                <CheckCircle2Icon className="size-3" />
                Withdrawals typically complete within a few minutes.
              </p>
            </div>

            <p className="text-xs text-muted-foreground">
              Note: Funds locked in open positions cannot be withdrawn.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWithdrawOpen(false)}
              disabled={isWithdrawing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleWithdraw}
              disabled={!withdrawAmount || !isAddressValid || isWithdrawing}
              className="text-white"
              style={{ backgroundColor: SITE_GREEN }}
            >
              {isWithdrawing ? (
                <>
                  <Loader2Icon className="size-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>Withdraw ${withdrawAmount || "0"} {selectedToken}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
